import { useTranslations } from '@dealerportal/i18n/client';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import type { FC } from 'react';

export interface AIPromptProps {
  onOptionClick: (option: string) => void;
  show: boolean;
  color: string;
}

export const AIPrompt: FC<AIPromptProps> = (props) => {
  const t = useTranslations('ui');

  return (
    <dialog
      className="ui-w-96 ui-z-10 ui-border-0 ui-h-full ui-bg-[#000000A3] ui-rounded-3xl ui-rounded-br-none"
      open={props.show}
    >
      <div className="ui-flex ui-flex-col ui-h-full">
        <div className="ui-flex-1" />
        <div
          className="ui-p-6 ui-rounded-t-xl"
          style={{ backgroundColor: props.color }}
        >
          <div className="ui-flex ui-flex-row ui-gap-3 ui-items-stretch">
            <div className="ui-w-16 ui-flex ui-items-center ui-justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="ui-text-[#F8F9FA] ui-h-auto ui-w-full"
              >
                <path
                  fillRule="evenodd"
                  d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 0 1-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 0 1-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 0 1-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584ZM12 18a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <p className="ui-font-semibold ui-mb-2 ui-text-[#F8F9FA]">
                {t('howCanWeHelp')}
              </p>
              <p className="ui-mb-4 ui-text-sm ui-text-[#F8F9FA]">
                {t('aiPromptDescription')}
              </p>
            </div>
          </div>
        </div>
        <div
          className={
            'ui-bg-white ui-flex ui-flex-col ui-items-center ui-w-full ui-px-6 ui-py-4 ui-rounded-bl-3xl'
          }
        >
          <BetterButton
            className={[
              'ui-w-full ui-mb-2 ui-text-white ui-py-3 ui-rounded-lg',
              { '!ui-w-max': false },
            ]}
            label={t('textAProfessional')}
            btnStyle="primary"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="size-5"
              >
                <path
                  fillRule="evenodd"
                  d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902 1.168.188 2.352.327 3.55.414.28.02.521.18.642.413l1.713 3.293a.75.75 0 0 0 1.33 0l1.713-3.293a.783.783 0 0 1 .642-.413 41.102 41.102 0 0 0 3.55-.414c1.437-.231 2.43-1.49 2.43-2.902V5.426c0-1.413-.993-2.67-2.43-2.902A41.289 41.289 0 0 0 10 2ZM6.75 6a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Zm0 2.5a.75.75 0 0 0 0 1.5h3.5a.75.75 0 0 0 0-1.5h-3.5Z"
                  clipRule="evenodd"
                />
              </svg>
            }
            attributes={{
              onClick: () => {
                props.onOptionClick('WEB2TEXT');
              },
            }}
          />
          <BetterButton
            className={[
              'ui-w-full ui-border ui-py-3 ui-rounded-lg',
              { '!ui-w-max': false },
            ]}
            label={t('askAI')}
            btnStyle="neutral"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="size-5"
              >
                <path d="M15.98 1.804a1 1 0 0 0-1.96 0l-.24 1.192a1 1 0 0 1-.784.785l-1.192.238a1 1 0 0 0 0 1.962l1.192.238a1 1 0 0 1 .785.785l.238 1.192a1 1 0 0 0 1.962 0l.238-1.192a1 1 0 0 1 .785-.785l1.192-.238a1 1 0 0 0 0-1.962l-1.192-.238a1 1 0 0 1-.785-.785l-.238-1.192ZM6.949 5.684a1 1 0 0 0-1.898 0l-.683 2.051a1 1 0 0 1-.633.633l-2.051.683a1 1 0 0 0 0 1.898l2.051.684a1 1 0 0 1 .633.632l.683 2.051a1 1 0 0 0 1.898 0l.683-2.051a1 1 0 0 1 .633-.633l2.051-.683a1 1 0 0 0 0-1.898l-2.051-.683a1 1 0 0 1-.633-.633L6.95 5.684ZM13.949 13.684a1 1 0 0 0-1.898 0l-.184.551a1 1 0 0 1-.632.633l-.551.183a1 1 0 0 0 0 1.898l.551.183a1 1 0 0 1 .633.633l.183.551a1 1 0 0 0 1.898 0l.184-.551a1 1 0 0 1 .632-.633l.551-.183a1 1 0 0 0 0-1.898l-.551-.184a1 1 0 0 1-.633-.632l-.183-.551Z" />
              </svg>
            }
            attributes={{
              onClick: () => {
                props.onOptionClick('AI');
              },
            }}
          />
        </div>
      </div>
    </dialog>
  );
};
